/** Normalize name for manager/team duplicate comparison */
const norm = (s) => (s || '').trim().toLowerCase();

/**
 * Placeholder team row where the manager is repeated under their own header
 * (team name matches manager, or manager is assigned as their own lead).
 * When a distinct lead exists, show that lead's team instead.
 */
export function isManagerSelfTeamRow(team, mgrGroup) {
  const mgrId = mgrGroup?.manager_id;
  if (!mgrId || team.manager_id !== mgrId) return false;

  // Real team with recruiters — show even when lead is also listed as manager
  const memberIds = team.member_ids
    || (team.members || []).map((m) => (typeof m === 'string' ? m : m.id)).filter(Boolean);
  const hasOtherMembers = memberIds.some(
    (id) => id && id !== mgrId && id !== team.lead_id && id !== team.assistant_manager_id,
  );
  if (hasOtherMembers) return false;

  if (team.lead_id && team.lead_id !== mgrId) return false;

  const mgrName = norm(mgrGroup.manager_name);
  const teamName = norm(team.name);
  return teamName === mgrName || team.lead_id === mgrId;
}

/** Teams to render under a manager header (excludes self-duplicate rows). */
export function getTeamsUnderManager(mgrGroup) {
  return (mgrGroup?.teams || []).filter((t) => !isManagerSelfTeamRow(t, mgrGroup));
}

/**
 * Orphan Excel-import bucket: no manager/lead on team doc but members are all
 * managers who already head their own teams — duplicates them under "Unassigned".
 */
export function isOrphanManagerBucketTeam(team, userMap = {}, allTeams = []) {
  if (team.manager_id || team.lead_id) return false;
  const memberIds = team.member_ids
    || (team.members || []).map((m) => (typeof m === 'string' ? m : m.id)).filter(Boolean);
  if (memberIds.length === 0) return false;

  const isManagerRole = (role) => /manager/.test(role || '');

  return memberIds.every((mid) => {
    const u = userMap[mid] || (team.members || []).find((m) => m.id === mid);
    if (!u || !isManagerRole(u.role)) return false;
    return allTeams.some(
      (t) => (t.id || t._id) !== (team.id || team._id) && t.manager_id === mid,
    );
  });
}

/** Hide orphan import teams with no manager/lead (empty shells or manager-only buckets). */
export function isOrphanUnassignedTeam(team, userMap = {}, allTeams = []) {
  if (team.manager_id || team.lead_id) return false;
  const memberIds = team.member_ids
    || (team.members || []).map((m) => (typeof m === 'string' ? m : m.id)).filter(Boolean);
  if (memberIds.length === 0) return true;
  return isOrphanManagerBucketTeam(team, userMap, allTeams);
}
